import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { RecordSection } from '../components/record/RecordPage.tsx';
import { useMailAccounts } from './useMailAccounts.ts';
import {
  deleteMailTouches,
  describeContactSync,
  disconnectMailAccount,
  formatDateTime,
  providerEnabled,
  providerLabels,
  scanMailTouches,
  setTouchTracking,
  startMailConnect,
  type MailAccount,
  type MailProvider
} from './mailIntegrations.ts';
import { getErrorMessage } from '../utils/helperUtils.ts';
import { isDemoSession } from '../apiClient.ts';
import { IMPORT_KINDS, importPath } from '../data-import/dataImportApi.ts';
import TeamSection from '../team/TeamSection.tsx';

// Provider error codes, translated into what the person can do about them.
const connectErrorMessage = (code: string, provider: MailProvider | null): string => {
  const vendor = provider === 'GMAIL' ? 'Google' : 'Microsoft';
  switch (code) {
    case 'access_denied':
      return provider === 'GMAIL'
        ? 'Google sign-in was cancelled or consent was declined. While the app is unverified, only addresses on its test-user list can connect.'
        : 'Microsoft sign-in was cancelled or consent was declined. If you saw "Need admin approval", a Microsoft 365 administrator for your organisation has to approve Forge reading mail before you can connect.';
    case 'invalid_state':
      return 'The connection link expired or was already used. Start again from this page.';
    case 'missing_code':
      return `${vendor} sent the browser back without an authorisation code. Try again.`;
    case 'token_exchange_failed':
      return `${vendor} did not complete the connection. Try again; if it keeps happening the API logs will say why.`;
    case 'unknown_provider':
      return 'That mail provider is not supported.';
    default:
      return `Connection failed (${code}).`;
  }
};

const providerFromSlug = (slug: string | null): MailProvider | null => {
  const upper = slug?.toUpperCase();
  return upper === 'OUTLOOK' || upper === 'GMAIL' ? upper : null;
};

type Banner = { severity: 'success' | 'error'; text: string };

type ProviderRowProps = {
  provider: MailProvider;
  account: MailAccount | null;
  enabled: boolean;
  busy: boolean;
  onConnect: (provider: MailProvider) => void;
  onDisconnect: (account: MailAccount) => void;
  children?: ReactNode;
};

const ProviderRow = ({
  provider,
  account,
  enabled,
  busy,
  onConnect,
  onDisconnect,
  children
}: ProviderRowProps) => {
  const label = providerLabels[provider];

  let status: { text: string; color: 'success' | 'warning' | 'default' };
  if (!enabled) status = { text: 'Coming soon', color: 'default' };
  else if (!account) status = { text: 'Not connected', color: 'default' };
  else if (account.needsReconnect) status = { text: 'Reconnect needed', color: 'warning' };
  else status = { text: 'Connected', color: 'success' };

  const lastScan = account?.lastScanAt
    ? `Last scan ${formatDateTime(account.lastScanAt)}: ${account.lastScanSummary ?? `${account.lastScanScanned ?? 0} messages, ${account.lastScanSuggested ?? 0} suggested`}`
    : account
      ? 'Not scanned yet'
      : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        py: 1.5,
        flexWrap: 'wrap',
        opacity: enabled ? 1 : 0.7
      }}
    >
      <Avatar variant="rounded" sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
        <MailOutlineIcon />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="subtitle1">{label}</Typography>
          <Chip size="small" label={status.text} color={status.color} />
        </Stack>
        {account ? (
          <>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {account.emailAddress}
              {account.displayName ? ` (${account.displayName})` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Connected {formatDateTime(account.createdAt)}
            </Typography>
            {lastScan ? (
              <Typography variant="body2" color="text.secondary">
                {lastScan}
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {describeContactSync(
                account,
                'Connected before address book access existed. Reconnect to allow contact sync.'
              )}
            </Typography>
            {account.needsReconnect ? (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                {account.lastScanError
                  ? `Microsoft no longer accepts the saved connection: ${account.lastScanError}`
                  : 'The saved connection has been idle for over 90 days and has most likely expired.'}{' '}
                Reconnect to keep scanning.
              </Typography>
            ) : account.lastScanStatus === 'error' && account.lastScanError ? (
              <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                Last scan failed: {account.lastScanError}
              </Typography>
            ) : null}
            {children}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {enabled
              ? `Connect ${label} so Forge can find the people being referred to you.`
              : `${label} support is not available yet.`}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1} sx={{ ml: { xs: 0, sm: 'auto' } }}>
        {!account ? (
          <Button
            variant="contained"
            size="small"
            disabled={!enabled || busy}
            onClick={() => onConnect(provider)}
          >
            Connect {label}
          </Button>
        ) : (
          <>
            {/* Reconnecting the same mailbox keeps its history; disconnecting deletes it. */}
            {account.needsReconnect || !account.canSyncContacts || !account.canReadCalendar ? (
              <Button
                variant={account.needsReconnect ? 'contained' : 'outlined'}
                size="small"
                disabled={busy}
                onClick={() => onConnect(provider)}
              >
                Reconnect
              </Button>
            ) : null}
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={busy}
              onClick={() => onDisconnect(account)}
            >
              Disconnect
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
};

const touchesLabel = (count: number) =>
  `${count.toLocaleString()} ${count === 1 ? 'touch' : 'touches'}`;

const touchScanLine = (account: MailAccount): { text: string; color: string } => {
  const at = formatDateTime(account.lastTouchScanAt);
  if (account.touchScanRunning) {
    return {
      text: account.lastTouchScanAt
        ? 'Scanning now.'
        : 'Reading the last 12 months of mail. This can take a few minutes; you can leave and come back.',
      color: 'text.secondary'
    };
  }
  if (!account.touchScanEnabled) {
    return {
      text: account.lastTouchScanAt
        ? `Off. Last scan ${at}.`
        : 'Off. Turning it on reads the last 12 months of mail.',
      color: 'text.secondary'
    };
  }
  if (!account.lastTouchScanAt) return { text: 'Not scanned yet.', color: 'text.secondary' };
  if (account.lastTouchScanStatus === 'error') {
    return {
      text: `Last scan ${at} failed: ${account.lastTouchScanError ?? 'unknown error'}`,
      color: 'error.main'
    };
  }
  if (account.lastTouchScanStatus === 'reconnect_needed') {
    return {
      text: `Last scan ${at} stopped: the mailbox needs to be reconnected.`,
      color: 'warning.main'
    };
  }
  return {
    text: `Last scan ${at}${account.lastTouchScanSummary ? `: ${account.lastTouchScanSummary}` : ''}`,
    color: 'text.secondary'
  };
};

type TouchTrackingProps = {
  account: MailAccount;
  busy: boolean;
  onToggle: (account: MailAccount, enabled: boolean) => void;
  onScan: (account: MailAccount) => void;
  onDeleteHistory: (account: MailAccount) => void;
};

const TouchTracking = ({
  account,
  busy,
  onToggle,
  onScan,
  onDeleteHistory
}: TouchTrackingProps) => {
  const running = account.touchScanRunning;
  const line = touchScanLine(account);

  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={account.touchScanEnabled}
            disabled={busy}
            onChange={(event) => onToggle(account, event.target.checked)}
          />
        }
        label="Relationship tracking"
        slotProps={{ typography: { variant: 'subtitle2' } }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Forge keeps who and when, never what: no subject, no preview, no body, and only for people
        already in your contacts. Disconnecting the mailbox deletes it.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
        {running ? <CircularProgress size={14} /> : null}
        <Typography variant="body2" color={line.color}>
          {line.text}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {account.touchCount
          ? `${touchesLabel(account.touchCount)} on record${account.touchScanEnabled ? '.' : ', kept until you delete them.'}`
          : 'No touches on record.'}
      </Typography>
      {account.touchScanEnabled && !account.canReadCalendar ? (
        <Typography variant="body2" color="text.secondary">
          Meetings are not counted yet. Reconnect to include your calendar.
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          disabled={busy || !account.touchScanEnabled || running || account.needsReconnect}
          onClick={() => onScan(account)}
        >
          {running ? 'Scanning' : 'Scan now'}
        </Button>
        <Button
          color="error"
          size="small"
          disabled={busy || !account.touchCount}
          onClick={() => onDeleteHistory(account)}
        >
          Delete relationship history
        </Button>
      </Stack>
    </Box>
  );
};

const providers: MailProvider[] = ['OUTLOOK', 'GMAIL'];

const SettingsPage = () => {
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accounts, loading, error, reload } = useMailAccounts();
  const [banner, setBanner] = useState<Banner | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingDisconnect, setPendingDisconnect] = useState<MailAccount | null>(null);
  const [trackingBusy, setTrackingBusy] = useState<number | null>(null);
  const [pendingHistoryDelete, setPendingHistoryDelete] = useState<MailAccount | null>(null);
  const demo = isDemoSession();

  // Touch scans run on the server. While one is going, reload the mailbox rows
  // three seconds after the last reload lands, until none is running.
  const touchScanRunning = accounts.some((a) => a.touchScanRunning);
  useEffect(() => {
    if (loading || !touchScanRunning) return;
    const timer = window.setTimeout(() => void reload(), 3000);
    return () => window.clearTimeout(timer);
  }, [loading, touchScanRunning, reload]);

  // Read the result of the OAuth round trip once, then clean the URL so a
  // refresh does not replay the banner.
  useEffect(() => {
    const connected = searchParams.get('connected');
    const errorCode = searchParams.get('error');
    const adminConsent = searchParams.get('adminConsent');
    const provider = providerFromSlug(searchParams.get('provider') ?? connected);
    if (!connected && !errorCode && !adminConsent) return;

    if (connected) {
      const label = provider ? providerLabels[provider] : connected;
      setBanner({ severity: 'success', text: `${label} is connected.` });
    } else if (adminConsent) {
      const tenant = searchParams.get('tenant');
      setBanner({
        severity: 'success',
        text: `Admin consent granted${tenant ? ` for tenant ${tenant}` : ''}. People in that organisation can now connect their mailbox.`
      });
    } else if (errorCode) {
      // The API says which step of the callback failed and why, so a bad
      // deploy can be read off this banner instead of the server logs.
      const stage = searchParams.get('stage');
      const detail = searchParams.get('detail');
      const stageLabel =
        stage === 'exchange'
          ? 'exchanging the code for tokens'
          : stage === 'profile'
            ? 'reading the mailbox identity'
            : stage === 'store'
              ? 'saving the connection'
              : null;
      const suffix = stageLabel
        ? ` Failed while ${stageLabel}${detail ? `: ${detail}` : '.'}`
        : detail
          ? ` ${detail}`
          : '';
      setBanner({
        severity: 'error',
        text: `${connectErrorMessage(errorCode, provider)}${suffix}`
      });
    }

    const next = new URLSearchParams(searchParams);
    next.delete('connected');
    next.delete('error');
    next.delete('adminConsent');
    next.delete('tenant');
    next.delete('provider');
    next.delete('stage');
    next.delete('detail');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConnect = async (provider: MailProvider) => {
    setBusy(true);
    try {
      await startMailConnect(provider);
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!pendingDisconnect) return;
    setBusy(true);
    try {
      await disconnectMailAccount(pendingDisconnect.id);
      notify(`${providerLabels[pendingDisconnect.provider]} disconnected`, { type: 'success' });
      await reload();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
      setPendingDisconnect(null);
    }
  };

  const handleToggleTracking = async (account: MailAccount, enabled: boolean) => {
    setTrackingBusy(account.id);
    try {
      await setTouchTracking(account.id, enabled);
      notify(`Relationship tracking is ${enabled ? 'on' : 'off'}.`, { type: 'success' });
      await reload();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    } finally {
      setTrackingBusy(null);
    }
  };

  const handleScanTouches = async (account: MailAccount) => {
    setTrackingBusy(account.id);
    try {
      await scanMailTouches(account.id);
      await reload();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    } finally {
      setTrackingBusy(null);
    }
  };

  const handleDeleteHistory = async () => {
    if (!pendingHistoryDelete) return;
    setTrackingBusy(pendingHistoryDelete.id);
    try {
      const { deleted } = await deleteMailTouches(pendingHistoryDelete.id);
      notify(`Deleted ${touchesLabel(deleted)}. Relationship tracking is off.`, {
        type: 'success'
      });
      await reload();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    } finally {
      setTrackingBusy(null);
      setPendingHistoryDelete(null);
    }
  };

  const visibleProviders = providers.filter((p) => providerEnabled[p] || p === 'GMAIL');

  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Settings</Typography>
      </Breadcrumbs>

      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.12em' }}
        >
          Account
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.25, fontWeight: 750, letterSpacing: '-0.025em' }}>
          Settings
        </Typography>
      </Box>

      {banner ? (
        <Alert severity={banner.severity} onClose={() => setBanner(null)} sx={{ mb: 2 }}>
          {banner.text}
        </Alert>
      ) : null}

      <RecordSection
        title="Connected Email"
        description="Forge reads a connected inbox to find the people being referred to you, then queues them for your review. Access is read-only: it can never send from or change your mailbox."
        action={
          <Button component={RouterLink} to="/contact-suggestions" size="small">
            Review suggestions
          </Button>
        }
      >
        {demo ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            Mailbox connections are turned off in the demo. Email Suggestions shows the kind of
            introductions a connected inbox finds.
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            Could not load connected mailboxes: {error}
          </Alert>
        ) : null}
        {loading && accounts.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading mailboxes
            </Typography>
          </Box>
        ) : visibleProviders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No email providers are enabled in this build.
          </Typography>
        ) : (
          <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
            {visibleProviders.map((provider) => {
              const account = accounts.find((a) => a.provider === provider) ?? null;
              return (
                <ProviderRow
                  key={provider}
                  provider={provider}
                  enabled={providerEnabled[provider]}
                  account={account}
                  // The demo shows the section but cannot connect; the API refuses too.
                  busy={busy || demo}
                  onConnect={handleConnect}
                  onDisconnect={setPendingDisconnect}
                >
                  {account ? (
                    <TouchTracking
                      account={account}
                      busy={trackingBusy === account.id}
                      onToggle={handleToggleTracking}
                      onScan={handleScanTouches}
                      onDeleteHistory={setPendingHistoryDelete}
                    />
                  ) : null}
                </ProviderRow>
              );
            })}
          </Stack>
        )}
      </RecordSection>

      <RecordSection
        title="Import data"
        description="Bring in contacts, companies and opportunities from a spreadsheet. Start from the template, or use the columns your file already has."
        sx={{ mt: 2 }}
      >
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {IMPORT_KINDS.map((entry) => (
            <Button
              key={entry.kind}
              component={RouterLink}
              to={importPath(entry.kind)}
              variant="outlined"
              size="small"
              startIcon={<UploadFileOutlinedIcon />}
            >
              {entry.label}
            </Button>
          ))}
        </Stack>
      </RecordSection>
      <TeamSection />

      <Dialog open={Boolean(pendingDisconnect)} onClose={() => setPendingDisconnect(null)}>
        <DialogTitle>Disconnect {pendingDisconnect?.emailAddress}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Forge deletes the saved connection, its relationship history, and any suggestions still
            waiting for review. Suggestions you already approved or rejected are kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDisconnect(null)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDisconnect} disabled={busy}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingHistoryDelete)} onClose={() => setPendingHistoryDelete(null)}>
        <DialogTitle>
          Delete relationship history for {pendingHistoryDelete?.emailAddress}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Forge deletes every touch recorded from this mailbox and turns relationship tracking
            off. Scores recompute without them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingHistoryDelete(null)} disabled={trackingBusy !== null}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteHistory}
            disabled={trackingBusy !== null}
          >
            Delete history
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;
